import { apiConfig, storageKeys } from 'config/app.config';
import { getCurrentAccessToken, isSupabaseAuthConfigured } from './auth/supabase.client';

export interface ApiResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export class ApiError extends Error {
  statusCode: number;
  details?: unknown;

  constructor(message: string, statusCode: number, details?: unknown) {
    super(message);
    this.statusCode = statusCode;
    this.details = details;
  }
}

async function request<T>(method: string, endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
  let token = localStorage.getItem(storageKeys.accessToken);
  if (isSupabaseAuthConfigured) {
    token = await getCurrentAccessToken();
    if (token) localStorage.setItem(storageKeys.accessToken, token);
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${apiConfig.baseUrl}${resolveEndpoint(endpoint)}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = response.status === 401 && isSupabaseAuthConfigured
      ? (token ? 'Your Supabase access token was rejected by the API. Sign out and sign in again.' : 'Your email confirmation or sign-in session is not complete. Confirm your email, then sign in before creating an organization.')
      : (payload.error || 'Request failed');
    throw new ApiError(message, response.status, payload.details);
  }
  return payload;
}

function resolveEndpoint(endpoint: string): string {
  const mappings: Array<[RegExp, string]> = [
    [/^\/me\/organizations/, '/organizations'],
    [/^\/orgs\/[^/]+\/stores\/[^/]+\/products/, '/catalog'],
    [/^\/orgs\/[^/]+\/stores\/[^/]+\/cost-documents/, '/cost-documents'],
    [/^\/orgs\/[^/]+\/suppliers/, '/catalog'],
    [/^\/orgs\/[^/]+\/imports/, '/imports'],
    [/^\/orgs\/[^/]+\/stores\/[^/]+\/(dashboard|data-health|analytics\/margins|analytics\/coverage)/, '/analytics'],
    [/^\/orgs\/[^/]+\/stores\/[^/]+\/(alerts|price-changes)|^\/orgs\/[^/]+\/alerts\//, '/alerts'],
    [/^\/orgs\/[^/]+\/stores\/[^/]+\/inventory/, '/inventory'],
    [/^\/orgs\/[^/]+\/stores\/[^/]+\/reports/, '/reports'],
    [/^\/orgs\/[^/]+\/members|^\/orgs\/[^/]+\/invitations|^\/invitations\//, '/users'],
    [/^\/orgs\/[^/]+\/notifications/, '/notifications'],
    [/^\/orgs\/[^/]+\/connections|^\/connections\/providers/, '/connect'],
    [/^\/orgs\/[^/]+\/billing/, '/billing'],
    [/^\/orgs(?:\/|$)/, '/organizations'],
  ];
  const mapping = mappings.find(([pattern]) => pattern.test(endpoint));
  return mapping ? `${mapping[1]}${endpoint}` : endpoint;
}

export const apiClient = {
  get: <T>(endpoint: string) => request<T>('GET', endpoint),
  post: <T>(endpoint: string, body?: unknown) => request<T>('POST', endpoint, body),
  patch: <T>(endpoint: string, body?: unknown) => request<T>('PATCH', endpoint, body),
  delete: <T>(endpoint: string) => request<T>('DELETE', endpoint),
};
